<template>
  <!-- 送信中オーバーレイ -->
  <div class="overlay" v-if="isSending">
    <div class="sending_box">
      <div class="spinner"></div>
      <p>送信中...</p>
    </div>
  </div>

  <!-- 送信完了画面 -->
  <div class="complete_wrapper" v-if="receiptNo">
    <div class="complete_card">
      <div class="complete_icon">✓</div>
      <h2>ご相談を受け付けました</h2>
      <p class="receipt">受付番号：<strong>{{ receiptNo }}</strong></p>
      <p>
        お送りいただいた内容は、相談窓口の担当者のみが確認します。<br>
        <template v-if="contactMethodSent !== '連絡不要'">ご希望の方法で、担当者より折り返しご連絡いたします。<br></template>
        ご相談いただいたことを理由に、選考などでご不利益な取扱いをすることは一切ありません。
      </p>
      <p class="note">追加でお伝えになりたいことがあれば、受付番号を添えて<br>{{ consultMail }} までご連絡ください。</p>
      <button class="btn_primary" @click="handleClose">トップへ戻る</button>
    </div>
  </div>

  <form class="form_card" @submit.prevent="submit" v-else>

    <!-- ヘッダー -->
    <div class="form_header">
      <button type="button" class="close_btn" @click="handleClose" aria-label="閉じる">✕</button>
      <img src="@/assets/mik_logo.svg" alt="MIK" class="form_logo">
      <h1 class="form_title">採用選考におけるハラスメント相談窓口</h1>
      <p class="form_subtitle">面接・面談などで不快な思いをされた方へ</p>
    </div>

    <div class="form_body">

      <!-- 方針 -->
      <section class="policy">
        <p>
          株式会社MIKは、面接・カジュアル面談・会社説明会・インターンシップなど、採用選考のあらゆる場面において、
          当社の従業員が応募者・求職者の皆さまに対して性的な言動（セクシュアルハラスメント）を行うことを一切認めません。
        </p>
        <p>
          たとえば次のようなことがあった場合は、どんな小さなことでもご相談ください。
        </p>
        <ul>
          <li>容姿・体型・服装、交際や結婚の予定など、性的・私的なことを聞かれた、話題にされた</li>
          <li>選考と関係のない食事・飲酒・個人的な連絡先の交換などに誘われた</li>
          <li>選考への影響をほのめかして、性的な誘いや要求をされた</li>
          <li>不必要に体に触れられた、性的な冗談・画像などで不快な思いをした</li>
        </ul>
        <div class="promise">
          <div class="promise_item"><span>秘密厳守</span>相談内容は窓口担当者のみが確認し、ご本人の同意なく選考担当者に伝えることはありません。</div>
          <div class="promise_item"><span>不利益なし</span>相談したことを理由に、選考などで不利益な取扱いをすることは一切ありません。</div>
          <div class="promise_item"><span>匿名も可</span>お名前・連絡先の入力は任意です（匿名の場合、折り返しのご連絡はできません）。</div>
        </div>
        <p class="direct">
          フォームのほか、メールでもご相談いただけます：
          <a :href="'mailto:' + consultMail">{{ consultMail }}</a>（MIKハラスメント対策委員会）
        </p>
      </section>

      <!-- ご相談者について -->
      <div class="section_label">ご相談者について</div>

      <div class="field">
        <label class="field_label" for="sh_type">あなたのお立場</label>
        <div class="select_wrapper">
          <select id="sh_type" v-model="applicant_type" class="field_select">
            <option value="">選択してください</option>
            <option v-for="t in APPLICANT_TYPES" :key="t" :value="t">{{ t }}</option>
          </select>
        </div>
      </div>

      <div class="field_row">
        <div class="field">
          <label class="field_label" for="sh_name">お名前 <span class="badge_optional">任意</span></label>
          <input id="sh_name" type="text" v-model="name" class="field_input" placeholder="匿名でも構いません" maxlength="100">
        </div>
        <div class="field">
          <label class="field_label" for="sh_phone">電話番号 <span class="badge_optional">任意</span></label>
          <input id="sh_phone" type="tel" v-model="phone" class="field_input" placeholder="例：090-0000-0000" maxlength="30">
        </div>
      </div>

      <div class="field">
        <label class="field_label" for="sh_mail">メールアドレス <span class="badge_optional">任意</span></label>
        <input id="sh_mail" type="email" v-model="mail" class="field_input" placeholder="例：tanaka@example.com" maxlength="100">
        <p class="hint">入力いただくと、受付番号をお知らせする確認メールをお送りします（相談内容は記載しません）。</p>
      </div>

      <div class="field">
        <span class="field_label">ご希望の連絡方法</span>
        <div class="radio_group">
          <label v-for="m in CONTACT_METHODS" :key="m" class="choice">
            <input type="radio" :value="m" v-model="contact_method"> {{ m }}
          </label>
        </div>
      </div>

      <!-- 事案について -->
      <div class="section_label" style="margin-top: 28px;">起きたことについて</div>
      <p class="section_note">わかる範囲・書ける範囲で構いません。</p>

      <div class="field_row">
        <div class="field">
          <label class="field_label" for="sh_when">いつ頃</label>
          <input id="sh_when" type="text" v-model="occurred_at" class="field_input" placeholder="例：9月20日 14時頃の一次面接" maxlength="100">
        </div>
        <div class="field">
          <label class="field_label" for="sh_scene">どの場面で</label>
          <div class="select_wrapper">
            <select id="sh_scene" v-model="scene" class="field_select">
              <option value="">選択してください</option>
              <option v-for="s in SCENES" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="field">
        <label class="field_label" for="sh_who">相手（わかる範囲で）</label>
        <input id="sh_who" type="text" v-model="counterpart" class="field_input" placeholder="例：面接官の男性（名前はわからない）" maxlength="300">
      </div>

      <div class="field">
        <label class="field_label" for="sh_detail">ご相談内容 <span class="badge_required">必須</span></label>
        <textarea id="sh_detail" v-model="detail" class="field_textarea" required maxlength="5000"
          placeholder="どのような言動があったか、どう感じたかなど、ご自由にお書きください。"></textarea>
        <p class="hint right">{{ detail.length }} / 5000</p>
      </div>

      <div class="field">
        <span class="field_label">希望する対応（複数選択可）</span>
        <div class="check_group">
          <label v-for="w in WISHES" :key="w" class="choice">
            <input type="checkbox" :value="w" v-model="wishes"> {{ w }}
          </label>
        </div>
        <input v-if="wishes.includes('その他')" type="text" v-model="wish_other" class="field_input" placeholder="ご希望をお書きください" maxlength="300">
      </div>

      <!-- ボット対策（非表示） -->
      <div class="hp" aria-hidden="true">
        <label>website<input type="text" v-model="website" tabindex="-1" autocomplete="off"></label>
      </div>

      <!-- 個人情報 -->
      <div class="privacy_box">
        <p class="privacy_title">個人情報の取扱いについて</p>
        <p>
          ご入力いただいた情報は、本相談への対応（事実確認、相談者への連絡、当社従業員への措置、再発防止策の検討）のためにのみ利用し、
          相談窓口（MIKハラスメント対策委員会）および対応に必要な最小限の担当者以外には開示しません。
          法令に基づく場合を除き、ご本人の同意なく第三者に提供することはありません。
          その他の取扱いは当社「<button type="button" class="pip_link" @click="isShowPIP = true">個人情報保護方針</button>」に従います。
        </p>
      </div>

      <div class="consent_area">
        <label class="consent_label">
          <input type="checkbox" v-model="agreed" class="consent_check">
          <span>上記の個人情報の取扱いに同意する</span>
        </label>
      </div>

      <p class="error" v-if="errorMessage" role="alert">{{ errorMessage }}</p>

      <TurnstileWidget ref="turnstile" @verified="t => turnstileToken = t" @expired="turnstileToken = ''" />

      <button type="submit" class="btn_submit" :disabled="!agreed || !detail.trim() || isSending || (turnstileRequired && !turnstileToken)">
        {{ isSending ? '送信中...' : '相談を送信する' }}
      </button>

      <p class="emergency">
        身の危険を感じる場合や、犯罪に当たるおそれのある行為を受けた場合は、ためらわず警察（110番）にもご相談ください。
      </p>
    </div>
  </form>

  <PersonalRuleModal v-if="isShowPIP" @close-pip="isShowPIP = false" />
</template>

<script>
import { ref } from 'vue';
import PersonalRuleModal from './PIP.vue';
import TurnstileWidget from './TurnstileWidget.vue';

const APPLICANT_TYPES = ["応募者（選考中）", "内定者", "インターンシップ参加者", "応募を検討中の方", "その他"];
const SCENES = ["面接（対面）", "面接（オンライン）", "カジュアル面談", "会社説明会", "インターンシップ", "メール・電話・SNS等での連絡", "その他"];
const WISHES = ["話を聞いてほしい", "事実確認と対応をしてほしい", "情報提供のみ（対応は求めない）", "その他"];
const CONTACT_METHODS = ["メール", "電話", "連絡不要"];

export default {
  components: { PersonalRuleModal, TurnstileWidget },
  emits: ['close_form'],
  setup(props, { emit }) {
    const consultMail = 'harassment@mik-s.jp';

    const applicant_type = ref("");
    const name = ref("");
    const mail = ref("");
    const phone = ref("");
    const contact_method = ref("メール");
    const occurred_at = ref("");
    const scene = ref("");
    const counterpart = ref("");
    const detail = ref("");
    const wishes = ref([]);
    const wish_other = ref("");
    const website = ref("");
    const openedAt = Date.now();
    const turnstile = ref(null);
    const turnstileToken = ref("");
    const turnstileRequired = !!process.env.VUE_APP_TURNSTILE_SITE_KEY;
    const agreed = ref(false);

    const isShowPIP = ref(false);
    const isSending = ref(false);
    const errorMessage = ref("");
    const receiptNo = ref("");
    const contactMethodSent = ref("");

    const validate = () => {
      if (!detail.value.trim()) return "ご相談内容を入力してください。";
      if (contact_method.value === "メール" && !mail.value.trim()) return "メールでの連絡をご希望の場合は、メールアドレスを入力してください。";
      if (contact_method.value === "電話" && !phone.value.trim()) return "電話での連絡をご希望の場合は、電話番号を入力してください。";
      return "";
    };

    const submit = async () => {
      if (isSending.value) return;
      errorMessage.value = validate();
      if (errorMessage.value) return;

      isSending.value = true;
      try {
        const response = await fetch('/api/harassment-consult', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            applicant_type: applicant_type.value,
            name: name.value,
            mail: mail.value,
            phone: phone.value,
            contact_method: contact_method.value,
            occurred_at: occurred_at.value,
            scene: scene.value,
            counterpart: counterpart.value,
            detail: detail.value,
            wishes: wishes.value,
            wish_other: wish_other.value,
            website: website.value,
            elapsed: Date.now() - openedAt,
            turnstileToken: turnstileToken.value,
            agreed: agreed.value,
          }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          errorMessage.value = result.message || `送信に失敗しました。お手数ですが ${consultMail} へ直接ご連絡ください。`;
          turnstile.value?.reset();
          return;
        }
        contactMethodSent.value = contact_method.value;
        receiptNo.value = result.receiptNo || "―";
        // 入力内容を画面から消去
        applicant_type.value = ""; name.value = ""; mail.value = ""; phone.value = "";
        occurred_at.value = ""; scene.value = ""; counterpart.value = ""; detail.value = "";
        wishes.value = []; wish_other.value = ""; agreed.value = false;
      } catch (e) {
        console.error(e);
        errorMessage.value = `通信エラーが発生しました。お手数ですが ${consultMail} へ直接ご連絡ください。`;
      } finally {
        isSending.value = false;
      }
    };

    const handleClose = () => {
      receiptNo.value = "";
      if (window.location.hash === '#harassment') {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      emit('close_form');
    };

    return {
      APPLICANT_TYPES, SCENES, WISHES, CONTACT_METHODS, consultMail,
      applicant_type, name, mail, phone, contact_method, occurred_at, scene,
      counterpart, detail, wishes, wish_other, website, agreed,
      turnstile, turnstileToken, turnstileRequired,
      isShowPIP, isSending, errorMessage, receiptNo, contactMethodSent,
      submit, handleClose,
    };
  }
};
</script>

<style scoped>
.form_card {
  width: 100%;
  max-width: 720px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  margin: 0 auto;
}

.form_header {
  background: linear-gradient(135deg, #3d5a73 0%, #2a4257 100%);
  padding: 32px 36px 28px;
  text-align: center;
  position: relative;
}
.close_btn {
  position: absolute; top: 16px; right: 18px;
  background: rgba(255,255,255,0.2); border: none; color: #fff;
  font-size: 18px; width: 34px; height: 34px; border-radius: 50%;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
}
.close_btn:hover { background: rgba(255,255,255,0.35); }
.form_logo { width: 120px; margin-bottom: 14px; filter: brightness(0) invert(1); }
.form_title { color: #fff; font-size: 20px; font-weight: bold; margin: 0 0 8px; }
.form_subtitle { color: rgba(255,255,255,0.85); font-size: 13px; margin: 0; }

.form_body { padding: 32px 36px 36px; }

.policy { font-size: 14px; line-height: 1.85; color: #333; margin-bottom: 32px; }
.policy p { margin: 0 0 12px; }
.policy ul { margin: 0 0 18px; padding-left: 20px; }
.policy li { margin-bottom: 4px; }
.promise { display: grid; gap: 10px; margin-bottom: 16px; }
.promise_item {
  background: #f4f8fb; border-radius: 10px; padding: 12px 16px;
  font-size: 13px; line-height: 1.7; color: #444;
}
.promise_item span {
  display: inline-block; font-weight: bold; color: #2a4257;
  background: #dfe9f1; border-radius: 4px; padding: 1px 8px; margin-right: 8px; font-size: 12px;
}
.direct { font-size: 13px; color: #555; }
.direct a { color: #0890a8; font-weight: bold; }

.section_label {
  font-size: 13px; font-weight: bold; color: #2a4257; letter-spacing: 0.08em;
  border-bottom: 2px solid #e6edf3; padding-bottom: 6px; margin-bottom: 18px;
}
.section_note { font-size: 12px; color: #888; margin: -12px 0 16px; }

.field_row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
.field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
.field_row .field { margin-bottom: 0; }
.field_label { font-size: 13px; font-weight: 600; color: #444; display: flex; align-items: center; gap: 6px; }
.badge_required { background: #ff6b6b; color: #fff; font-size: 10px; font-weight: bold; padding: 2px 6px; border-radius: 3px; }
.badge_optional { background: #b8c4ce; color: #fff; font-size: 10px; font-weight: bold; padding: 2px 6px; border-radius: 3px; }
.hint { font-size: 12px; color: #888; margin: 0; }
.hint.right { text-align: right; }

.field_input, .field_select, .field_textarea {
  width: 100%; box-sizing: border-box; padding: 10px 14px;
  border: 1.5px solid #dde1e7; border-radius: 8px; font-size: 15px; color: #333;
  background: #fafbfc; outline: none; font-family: inherit;
  transition: border-color 200ms, box-shadow 200ms;
}
.field_input:focus, .field_select:focus, .field_textarea:focus {
  border-color: #3d5a73; box-shadow: 0 0 0 3px rgba(61, 90, 115, 0.12); background: #fff;
}
.field_input::placeholder, .field_textarea::placeholder { color: #bbb; }
.field_textarea { height: 180px; resize: vertical; }

.select_wrapper { position: relative; }
.select_wrapper::after {
  content: '▾'; position: absolute; right: 12px; top: 50%;
  transform: translateY(-50%); color: #888; pointer-events: none; font-size: 14px;
}
.field_select { appearance: none; -webkit-appearance: none; padding-right: 36px; }

.radio_group, .check_group { display: flex; flex-wrap: wrap; gap: 8px 20px; }
.choice { font-size: 14px; color: #444; display: flex; align-items: center; gap: 6px; cursor: pointer; }
.choice input { accent-color: #3d5a73; width: 16px; height: 16px; }

.hp { position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden; }

.privacy_box {
  background: #f7f9fc; border-radius: 10px; padding: 14px 18px; margin: 12px 0 12px;
  font-size: 12px; line-height: 1.8; color: #555;
}
.privacy_box p { margin: 0; }
.privacy_title { font-weight: bold; color: #333; margin-bottom: 4px !important; }
.pip_link {
  background: none; border: none; color: #0890a8; font-weight: bold;
  cursor: pointer; padding: 0; font-size: 12px; text-decoration: underline;
}

.consent_area { padding: 6px 4px; margin-bottom: 20px; }
.consent_label { display: flex; align-items: center; gap: 10px; font-size: 14px; color: #444; cursor: pointer; }
.consent_check { width: 18px; height: 18px; accent-color: #3d5a73; flex-shrink: 0; }

.error {
  background: #fff1f1; border: 1px solid #ffc9c9; color: #c0392b;
  border-radius: 8px; padding: 10px 14px; font-size: 13px; margin: 0 0 16px;
}

.btn_submit {
  width: 100%; height: 52px;
  background: linear-gradient(135deg, #3d5a73, #2a4257);
  color: #fff; font-size: 17px; font-weight: bold; border: none; border-radius: 12px;
  cursor: pointer; letter-spacing: 0.05em; transition: 200ms;
}
.btn_submit:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(42,66,87,0.3); }
.btn_submit:disabled { background: #c8d0d8; cursor: not-allowed; }

.emergency { font-size: 12px; color: #888; line-height: 1.7; margin: 18px 0 0; }

.btn_primary {
  padding: 12px 36px; background: linear-gradient(135deg, #3d5a73, #2a4257);
  color: #fff; font-size: 15px; font-weight: bold; border: none; border-radius: 10px;
  cursor: pointer; margin-top: 20px;
}

.overlay {
  position: fixed; inset: 0; background: rgba(255,255,255,0.85);
  display: flex; align-items: center; justify-content: center; z-index: 9999;
}
.sending_box { text-align: center; display: flex; flex-direction: column; align-items: center; gap: 16px; color: #555; }
.spinner {
  width: 48px; height: 48px; border: 4px solid #e6edf3; border-top-color: #3d5a73;
  border-radius: 50%; animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.complete_wrapper {
  position: fixed; inset: 0; background: #f4f8fb;
  display: flex; align-items: center; justify-content: center; z-index: 9998; padding: 16px;
}
.complete_card {
  background: #fff; border-radius: 20px; padding: 48px 40px; text-align: center;
  box-shadow: 0 8px 40px rgba(0,0,0,0.1); max-width: 480px;
}
.complete_icon {
  width: 64px; height: 64px; background: linear-gradient(135deg, #3d5a73, #2a4257);
  color: #fff; font-size: 28px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; margin: 0 auto 20px;
}
.complete_card h2 { font-size: 20px; color: #2c3e50; margin: 0 0 12px; }
.complete_card p { font-size: 14px; color: #666; line-height: 1.8; margin: 0 0 10px; }
.complete_card .receipt { font-size: 15px; color: #333; }
.complete_card .note { font-size: 12px; color: #888; }

@media (max-width: 600px) {
  .form_card { border-radius: 0; box-shadow: none; }
  .form_header { padding: 24px 20px 20px; }
  .form_body { padding: 24px 20px 28px; }
  .field_row { grid-template-columns: 1fr; }
  .form_title { font-size: 17px; }
  .form_logo { width: 100px; }
  .complete_card { padding: 36px 22px; }
}
</style>
